<script lang="ts" setup>
import FacilityInfo from "@/views/trips/components/FacilityInfo.vue";
import OrderStopStatus from "@/components/status/OrderStopStatus.vue";
import AlertMessage from "@/components/AlertMessage.vue";
import OrderStopFreightDetails from "@/views/trips/components/OrderStopFreightDetails.vue";
import StopChangesPopover from "@/views/trips/components/StopChangesPopover.vue";
import { useOrder } from "@/composables/useOrder";
import type { OrderFreightDto } from "@/api/orders/dto/order.dto";
import type {
  TripOrderStop,
  TripReloadStop,
  TripRoute,
  TripRouteFreight,
  TripRouteStop,
} from "@/views/trips/edit/trip";
import { computed } from "vue";
import { OrderFreightState, OrderStopState, OrderStopType } from "@/data/order";
import { useAppStore } from "@/store/app.store";
import PackageVariantClose from "@/assets/images/package-variant-closed.svg";
import { RejectedRouteStatuses } from "@/data/trip";

const {
  STOP_RELOAD,
  STOP_DELIVERY,
  STOP_PICK_UP,
  getFreightName,
  timeTypesMap,
  getStopTime,
  isReloadStop,
  createOrderFreightFromRouteFreight,
  createStopName,
} = useOrder();

const props = withDefaults(
  defineProps<{
    orderFreights: OrderFreightDto[];
    routes: TripRoute[];
    stop: TripOrderStop;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  },
);

const freights = computed<OrderFreightDto[]>(() => {
  if (isReloadStop(props.stop)) {
    return props.stop.reloadFreights.map((f) => {
      return createOrderFreightFromRouteFreight(f);
    });
  }

  const freights = [];
  for (const freight of props.orderFreights) {
    if (props.stop.freights && props.stop.freights.includes(freight.id || "") && freight) {
      freights.push({ ...freight, name: getFreightName(freight, freight.number, "", false) });
    }
  }

  return freights;
});

const isPickup = computed(() => {
  return props.stop.type === OrderStopType.pickup;
});

const isDeleted = computed(() => {
  return props.stop.isDeleted;
});

const isUpdated = computed(() => {
  return (
    !isDeleted.value &&
    (props.stop.state === OrderStopState.updated ||
      freights.value.some((f) => f.state !== OrderFreightState.normal || f.isDeleted))
  );
});

const activeRouteFreights = computed<TripRouteFreight[]>(() => {
  return props.routes.filter((route) => !RejectedRouteStatuses.includes(route.status)).flatMap((r) => r.freights);
});

const activeRouteStops = computed<TripRouteStop[]>(() => {
  return props.routes
    .filter((route) => !RejectedRouteStatuses.includes(route.status))
    .flatMap((route) => route.routeStops);
});

const mismatch = computed(() => {
  if (!activeRouteFreights.value.length || !freights.value.length || props.stop.type === OrderStopType.delivery) {
    return null;
  }

  const notAllocatedFreights = [];
  const mismatchFreights = [];
  for (const orderStopFreight of freights.value) {
    const relatedRouteFreights = activeRouteFreights.value.filter(
      (f) => f.freightId === orderStopFreight.id && !f.parentFreightId,
    ); // reload freights does not count

    if (relatedRouteFreights.length === 0) {
      notAllocatedFreights.push(orderStopFreight);
    } else {
      const allocatedQuantity = relatedRouteFreights.reduce((acc, f) => acc + Number(f.plannedQuantity), 0);
      const allocatedWeight = relatedRouteFreights.reduce((acc, f) => acc + Number(f.plannedWeight), 0);
      if (allocatedQuantity !== orderStopFreight.quantity || allocatedWeight !== orderStopFreight.weight) {
        const mismatchChunks = [];
        const quantityDiff = allocatedQuantity - orderStopFreight.quantity;
        const weightDiff = allocatedWeight - orderStopFreight.weight;
        if (quantityDiff > 0) {
          mismatchChunks.push(`selected additional ${quantityDiff} ${orderStopFreight.type}`);
        }
        if (quantityDiff < 0) {
          mismatchChunks.push(`not selected ${-quantityDiff} ${orderStopFreight.type}`);
        }
        if (weightDiff > 0) {
          mismatchChunks.push(`selected additional ${weightDiff} lbs`);
        }
        if (weightDiff < 0) {
          mismatchChunks.push(`not selected ${-weightDiff} lbs`);
        }
        mismatchFreights.push({
          freightNumber: orderStopFreight.number,
          diff: mismatchChunks.join(", "),
        });
      }
    }
  }

  return {
    notAllocatedFreights,
    mismatchFreights,
  };
});

const reloadFreightsMismatch = computed<{ notAllocatedFreights: TripRouteFreight[] } | null>(() => {
  if (!isReloadStop(props.stop)) {
    return null;
  }

  const currentStop: TripReloadStop = props.stop;

  const reloadPickUpsFreights = activeRouteStops.value
    .filter(
      (routeStop) =>
        routeStop.reloadStopId === currentStop.id && routeStop.isReload && routeStop.type === OrderStopType.pickup,
    )
    .flatMap((stop) => stop.freights);

  const relatedRouteFreights = activeRouteFreights.value
    .filter((freight) => {
      return reloadPickUpsFreights.includes(freight.id);
    })
    .flatMap((freight) => freight.parentFreightId);

  const notAllocatedFreights: TripRouteFreight[] = currentStop.reloadFreights.filter((reloadFreight) => {
    return !relatedRouteFreights.includes(reloadFreight.id);
  });

  return {
    notAllocatedFreights,
  };
});

const deletedFreights = computed(() => {
  return freights.value.filter((f) => f.isDeleted);
});

const lastEditBy = computed(() => {
  const appStore = useAppStore();
  if (props.stop.lastEditedBy) {
    return (appStore.getDispatcher(props.stop.lastEditedBy) || {}).name;
  }

  return "";
});

const showTime = computed(() => {
  if (isReloadStop(props.stop)) {
    return props.stop.isNew && props.stop.timeType;
  }

  return props.stop.timeType;
});

const name = computed(() => {
  const suffix = props.stop.isReload ? "stop" : "";
  return createStopName(props.stop.type, props.stop?.number || 1, suffix);
});

function getFreightClass(freight: OrderFreightDto) {
  return {
    "freight--new": freight.state === OrderFreightState.new,
    "freight--updated": freight.state === OrderFreightState.updated,
    "freight--deleted": freight.isDeleted,
  };
}
</script>

<template>
  <div
    :class="{ 'reload-stop': stop.type === STOP_RELOAD, 'updated-stop': isUpdated, 'deleted-stop': isDeleted }"
    class="trip-stops__block"
  >
    <div :class="disabled ? 'bg-red-lighten-5' : 'bg-grey-lighten-4'" class="trip-stops__block-inner pa-4 rounded">
      <div class="mb-6 trip-stops__block-header">
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="d-flex align-center flex-wrap">
            <v-icon v-if="stop.type === STOP_PICK_UP" :color="isDeleted ? 'grey' : 'green'" class="mr-2" size="20">
              mdi-package-variant-closed
            </v-icon>
            <v-icon v-if="stop.type === STOP_RELOAD" class="mr-2" color="orange" size="20">
              mdi-package-variant-closed
            </v-icon>
            <v-icon
              v-if="stop.type === STOP_DELIVERY"
              :color="isDeleted ? 'grey' : 'red accent-2'"
              class="mr-2"
              size="20"
            >
              mdi-map-marker-radius-outline
            </v-icon>
            <span
              :class="isDeleted ? 'text-grey' : 'text-grey-darken-1'"
              class="font-weight-600 mr-3 font-size-12"
              data-qa="stop-type"
            >
              {{ name }}
            </span>
            <router-link
              v-if="stop.order && !isDeleted"
              :to="{ name: 'order-view', params: { id: stop.order.id } }"
              class="font-size-12 font-weight-medium text-primary text-decoration-none"
              target="_blank"
            >
              Order #{{ stop.order?.number }}
            </router-link>
            <span v-if="isDeleted" class="text-grey font-size-12 font-weight-medium">
              Order #{{ stop.order?.number }}
            </span>
            <stop-changes-popover
              v-if="isUpdated"
              :freights="freights"
              :stop="stop"
              label="Order stop changes"
            ></stop-changes-popover>
          </div>
          <div v-if="isUpdated" class="badge bg-warning pa-2 rounded-te rounded-bs font-weight-600">UPDATED</div>
          <div v-if="isDeleted" class="badge bg-grey-darken-1 pa-2 rounded-te rounded-bs font-weight-600">DELETED</div>
        </div>

        <order-stop-status v-if="!isDeleted" :stop="stop"></order-stop-status>
      </div>

      <div v-if="!isDeleted" class="mb-6 trip-stops__block-body">
        <facility-info
          v-if="stop.facility"
          :facility="stop.facility"
          :updated="isUpdated && !!stop.oldFacilityId && !disabled"
          class="mb-5"
        ></facility-info>
        <div v-if="showTime" class="d-flex align-center text-grey-darken-1">
          <v-icon class="mr-2" size="20">mdi-clock-outline</v-icon>
          <span class="font-weight-medium" data-qa="stop-time">
            {{ timeTypesMap[stop.timeType] }}: {{ getStopTime(stop) }}
          </span>
        </div>
      </div>
      <div v-if="!isDeleted" class="trip-stops__block-footer">
        <div class="d-flex">
          <div class="text-grey-darken-1 mr-2">Freights:</div>
          <div
            v-if="freights.length"
            :class="disabled ? 'text-error' : 'text-grey-darken-2'"
            class="font-weight-medium"
            data-qa="freight-size"
          >
            <div
              v-for="freight in freights"
              :key="freight.id"
              :class="getFreightClass(freight)"
              class="freight d-flex align-center"
            >
              <span>
                {{ freight.name }}
              </span>

              <package-variant-close v-if="freight.stackable && isPickup" class="ml-2" />
            </div>
          </div>
          <span v-else class="text-grey-darken-1 font-weight-500">No added</span>
        </div>

        <template v-if="mismatch && !isReloadStop(stop)">
          <template v-if="mismatch.notAllocatedFreights.length > 0">
            <alert-message v-for="freight in mismatch.notAllocatedFreights" :key="freight.id" class="mt-3">
              Add Freight #{{ freight.number }} to any PU in route(s)
            </alert-message>
          </template>

          <template v-if="mismatch?.mismatchFreights.length > 0">
            <alert-message
              v-for="mismatchData in mismatch.mismatchFreights"
              :key="mismatchData.freightNumber"
              class="mt-3"
              data-qa="updated-freight-data-message"
            >
              Freight #{{ mismatchData.freightNumber }} mismatch: {{ mismatchData.diff }}
            </alert-message>
          </template>
        </template>

        <template v-else-if="reloadFreightsMismatch && reloadFreightsMismatch.notAllocatedFreights.length">
          <alert-message align="top" class="mt-3">
            Freights:
            <br />
            <div v-for="item of reloadFreightsMismatch.notAllocatedFreights" :key="item.id">
              <span>{{ item.name }}</span>
              <br />
            </div>
            are not selected at any PU reload stops
          </alert-message>
        </template>

        <alert-message
          v-for="deletedFreight in deletedFreights"
          :key="deletedFreight.id"
          bg-color="indigo-lighten-5"
          border-color="none"
          class="mt-3"
          color="indigo"
          icon="mdi-information-outline"
          icon-color="indigo"
        >
          Freight #{{ deletedFreight.number }} will be removed after re-dispatch confirmation
        </alert-message>

        <order-stop-freight-details v-if="isPickup && freights.length > 1" :freights="freights">
        </order-stop-freight-details>
      </div>
      <div v-if="isDeleted" class="position-relative h-100" style="margin-top: -40px">
        <div class="absolute-center text-grey font-size-16 text-center">Stop was deleted<br />by {{ lastEditBy }}</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/color";

.trip-stops__block-inner {
  height: 100%;
}

.updated-stop .trip-stops__block-inner {
  border: 1px solid $warning;
}

.deleted-stop {
  .trip-stops__block-inner {
    border: 1px solid $grey-light;
  }
}

.badge {
  margin-top: -16px;
  margin-right: -16px;
}

.freight {
  position: relative;
  width: max-content;

  &:after {
    position: absolute;
    content: "";
    display: none;
    top: 50%;
    transform: translateY(-50%);
    width: 6px;
    height: 6px;
    border-radius: 50%;
    text-decoration: none !important;
    right: -14px;
  }
}

.freight--deleted {
  text-decoration-line: line-through;
  text-decoration-color: $grey-lighter-contrast;
  color: $grey-lighter-contrast !important;

  &:after {
    display: block;
    background-color: $grey-lighter-contrast;
    color: $grey-lighter-contrast;
  }
}

.freight--updated {
  &:after {
    display: block;
    background-color: orange;
  }
}

.freight--new {
  &:after {
    display: block;
    background-color: green;
  }
}
</style>
